import { useEffect } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { Tenant } from '@core/Entities/App/Tenants.entity';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';
import { useAtomValue } from 'jotai';

import { Model, normalizedModelAtom } from 'Atoms/Search/AggregationsAtom/NormalizedMakesAtom';
import { Item } from 'Components/MultiSelect/MultiSelect.entity';
import { Logos } from 'Components/SearchWidget/Components/MakeLabel/Logos';
import MakeLabel from 'Components/SearchWidget/Components/MakeLabel/MakeLabel';
import { useFilterLink } from 'Hooks/SearchFilters/UseFilterLink';
import { useSearchFilter } from 'Hooks/SearchFilters/UseSearchFilter';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';
import { getArrayFilterValue } from 'Utils/SearchFilters/GetArrayFilterValue';

import { FilterLink } from '../FilterLink/FilterLink';

const mapVariants = (
  availableVariantsAtom: VariantAtom[],
  models: Record<string, Model>,
  getLinkForFilterValue: ReturnType<typeof useFilterLink>['getLinkForFilterValue'],
): Item[] => {
  if (Object.keys(models).length === 0) return [];

  return availableVariantsAtom.map(variant => {
    const maybeLink = getLinkForFilterValue({
      filterValue: variant.key,
      resultCount: variant.count,
    });

    return {
      id: variant.key,
      renderLabel: (
        <MakeLabel
          count={variant.count}
          make={variant.make as Logos}
          name={variant.displayName}
          nameRender={<FilterLink link={maybeLink}>{variant.displayName}</FilterLink>}
        />
      ),
      label: variant.displayName,
      model: variant.modelDisplayName + ' variants',
    };
  });
};

const useModelVariant = (
  locale: Locale,
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
) => {
  const FILTER_ID = 'variant-type';
  const filterList = useAtomValue(filtersListAtom);
  const normalizedModels = useAtomValue(normalizedModelAtom);
  const activeModels = getArrayFilterValue('model', filterList) || [];
  const activeModelVariants = getArrayFilterValue(FILTER_ID, filterList) || [];
  const { updateFilters } = useUpdateFilters();
  const { getLinkForFilterValue } = useFilterLink({
    filterId: FILTER_ID,
    locale,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  useEffect(() => {
    const disallowedVariants = activeModelVariants.filter(variantId => {
      const variantIdParts = variantId.split('-');
      return !activeModels.includes(`${variantIdParts[0]}-${variantIdParts[1]}`);
    });

    if (disallowedVariants.length > 0) {
      const allowedVariants = activeModelVariants.filter(
        variantId => !disallowedVariants.includes(variantId),
      );
      updateFilters([{ id: FILTER_ID, value: allowedVariants }]);
    }
  }, [activeModels]);

  const variants = activeModels.reduce((acc, models) => {
    const variants = normalizedModels[models]?.trimsPerModel ?? [];
    return [...acc, ...variants];
  }, [] as VariantAtom[]);

  const allVariants = mapVariants(variants, normalizedModels, getLinkForFilterValue);

  return useSearchFilter({ filterId: FILTER_ID, data: allVariants });
};

export default useModelVariant;
